import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import apiService from '../../services/api';

function HotelListings() {
    const [hotels, setHotels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchHotels();
    }, []);

    const fetchHotels = async () => {
        try {
            setLoading(true);
            
            // Try to fetch accommodations from different endpoints
            let response = null;
            
            try {
                // Try Airbnb accommodations first
                response = await apiService.getAccommodations();
            } catch (airbnbError) {
                try {
                    // Try featured accommodations
                    response = await apiService.getFeaturedAccommodations();
                } catch (featuredError) {
                    // Fallback to regular accommodations
                    console.log('Using fallback hotel data - API not available');
                }
            }

            if (response && response.results && response.results.length > 0) {
                // Transform accommodation data to hotel format
                const hotelData = response.results.map((accommodation) => ({
                    id: accommodation.id,
                    name: accommodation.name || accommodation.title,
                    image: accommodation.main_image || accommodation.featured_image || '/assets/img/hotel/hotel_1.jpg',
                    rating: accommodation.rating || 4.5,
                    price: accommodation.price_per_night || accommodation.price || 120,
                    location: accommodation.location || accommodation.address || 'Prime Location',
                    amenities: accommodation.amenities || ['WiFi', 'Pool', 'Restaurant'],
                    description: accommodation.short_description || accommodation.description || 'Comfortable accommodation with excellent facilities',
                }));
                setHotels(hotelData);
            } else {
                // Use fallback data
                setHotels([
                    {
                        id: 1,
                        name: "Grand Luxury Hotel",
                        image: "/assets/img/hotel/hotel_1.jpg",
                        rating: 4.8,
                        price: 250,
                        location: "City Center",
                        amenities: ["WiFi", "Pool", "Spa", "Restaurant"],
                        description: "Experience luxury at its finest with our premium accommodations and world-class service.",
                    },
                    {
                        id: 2,
                        name: "Seaside Resort & Spa",
                        image: "/assets/img/hotel/hotel_2.jpg",
                        rating: 4.6,
                        price: 180,
                        location: "Beachfront",
                        amenities: ["WiFi", "Beach Access", "Pool", "Spa"],
                        description: "Relax by the ocean with stunning views and exceptional resort amenities.",
                    },
                    {
                        id: 3,
                        name: "Mountain View Lodge",
                        image: "/assets/img/hotel/hotel_3.jpg",
                        rating: 4.4,
                        price: 120,
                        location: "Mountain View",
                        amenities: ["WiFi", "Hiking Trails", "Restaurant", "Fireplace"],
                        description: "Perfect retreat in the mountains with breathtaking scenery and cozy accommodations.",
                    },
                    {
                        id: 4,
                        name: "Business Class Hotel",
                        image: "/assets/img/hotel/hotel_4.jpg",
                        rating: 4.3,
                        price: 150,
                        location: "Business District",
                        amenities: ["WiFi", "Business Center", "Gym", "Restaurant"],
                        description: "Ideal for business travelers with modern facilities and convenient location.",
                    },
                    {
                        id: 5,
                        name: "Boutique Heritage Hotel",
                        image: "/assets/img/hotel/hotel_5.jpg",
                        rating: 4.7,
                        price: 200,
                        location: "Historic Quarter",
                        amenities: ["WiFi", "Heritage Tours", "Restaurant", "Rooftop Bar"],
                        description: "Unique heritage experience with modern comfort in a historic setting.",
                    },
                    {
                        id: 6,
                        name: "Family Resort Paradise",
                        image: "/assets/img/hotel/hotel_6.jpg",
                        rating: 4.5,
                        price: 160,
                        location: "Resort Area",
                        amenities: ["WiFi", "Kids Club", "Pool", "Multiple Restaurants"],
                        description: "Perfect family destination with activities for all ages and excellent facilities.",
                    }
                ]);
            }
            setError(null);
        } catch (err) {
            console.error('Error fetching hotels:', err);
            setError('Failed to load hotels');
            // Use fallback data
            setHotels([
                {
                    id: 1,
                    name: "Grand Luxury Hotel",
                    image: "/assets/img/hotel/hotel_1.jpg",
                    rating: 4.8,
                    price: 250,
                    location: "City Center",
                    amenities: ["WiFi", "Pool", "Spa", "Restaurant"],
                    description: "Experience luxury at its finest with our premium accommodations and world-class service.",
                },
                {
                    id: 2,
                    name: "Seaside Resort & Spa",
                    image: "/assets/img/hotel/hotel_2.jpg",
                    rating: 4.6,
                    price: 180,
                    location: "Beachfront",
                    amenities: ["WiFi", "Beach Access", "Pool", "Spa"],
                    description: "Relax by the ocean with stunning views and exceptional resort amenities.",
                },
                {
                    id: 3,
                    name: "Mountain View Lodge",
                    image: "/assets/img/hotel/hotel_3.jpg",
                    rating: 4.4,
                    price: 120,
                    location: "Mountain View",
                    amenities: ["WiFi", "Hiking Trails", "Restaurant", "Fireplace"],
                    description: "Perfect retreat in the mountains with breathtaking scenery and cozy accommodations.",
                },
                {
                    id: 4,
                    name: "Business Class Hotel",
                    image: "/assets/img/hotel/hotel_4.jpg",
                    rating: 4.3,
                    price: 150,
                    location: "Business District",
                    amenities: ["WiFi", "Business Center", "Gym", "Restaurant"],
                    description: "Ideal for business travelers with modern facilities and convenient location.",
                },
                {
                    id: 5,
                    name: "Boutique Heritage Hotel",
                    image: "/assets/img/hotel/hotel_5.jpg",
                    rating: 4.7,
                    price: 200,
                    location: "Historic Quarter",
                    amenities: ["WiFi", "Heritage Tours", "Restaurant", "Rooftop Bar"],
                    description: "Unique heritage experience with modern comfort in a historic setting.",
                },
                {
                    id: 6,
                    name: "Family Resort Paradise",
                    image: "/assets/img/hotel/hotel_6.jpg",
                    rating: 4.5,
                    price: 160,
                    location: "Resort Area",
                    amenities: ["WiFi", "Kids Club", "Pool", "Multiple Restaurants"],
                    description: "Perfect family destination with activities for all ages and excellent facilities.",
                }
            ]);
        } finally {
            setLoading(false);
        }
    };

    // Function to render star rating
    const renderStarRating = (rating) => {
        const stars = [];
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 !== 0;

        for (let i = 0; i < fullStars; i++) {
            stars.push(<i key={i} className="fas fa-star text-warning"></i>);
        }

        if (hasHalfStar) {
            stars.push(<i key="half" className="fas fa-star-half-alt text-warning"></i>);
        }

        const emptyStars = 5 - Math.ceil(rating);
        for (let i = 0; i < emptyStars; i++) {
            stars.push(<i key={`empty-${i}`} className="far fa-star text-muted"></i>);
        }

        return stars;
    };

    if (loading) {
        return (
            <section className="space">
                <div className="container">
                    <div className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading hotels...</span>
                        </div>
                        <p className="mt-3 text-muted">Finding the best accommodations for you...</p>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="space">
            <div className="container">
                {error && (
                    <div className="alert alert-warning text-center mb-4">
                        <i className="fas fa-exclamation-triangle me-2"></i>
                        {error} - Showing demo hotels
                    </div>
                )}

                <div className="title-area text-center mb-5">
                    <span className="sub-title">Accommodations</span>
                    <h2 className="sec-title">Featured Hotels & Resorts</h2>
                    <p className="sec-text">
                        Discover our handpicked selection of premium accommodations for your perfect stay
                    </p>
                </div>

                <div className="row gy-4 gx-4">
                    {hotels.length > 0 ? (
                        hotels.map((hotel) => (
                            <div key={hotel.id} className="col-xl-4 col-lg-6 col-md-6">
                                <div className="tour-box th-ani">
                                    <div className="tour-box_img global-img">
                                        <img 
                                            src={hotel.image} 
                                            alt={hotel.name}
                                            onError={(e) => {
                                                e.target.src = '/assets/img/hotel/hotel_1.jpg';
                                            }}
                                        />
                                        <div className="tour-rating">
                                            <i className="fa-solid fa-star"></i>
                                            <span>{hotel.rating}</span>
                                        </div>
                                    </div>
                                    <div className="tour-content">
                                        <h3 className="box-title">
                                            <Link to={`/hotel-details/${hotel.id}`}>{hotel.name}</Link>
                                        </h3>
                                        <div className="tour-meta">
                                            <span><i className="fas fa-map-marker-alt"></i> {hotel.location}</span>
                                        </div>
                                        <p className="tour-text">{hotel.description}</p>
                                        
                                        <div className="tour-amenities mb-3">
                                            {hotel.amenities.slice(0, 3).map((amenity, index) => (
                                                <span key={index} className="amenity-tag">
                                                    <i className="fas fa-check"></i> {amenity}
                                                </span>
                                            ))}
                                        </div>

                                        <div className="tour-rating mb-2">
                                            {renderStarRating(hotel.rating)}
                                            <span className="rating-text ms-2">({hotel.rating})</span>
                                        </div>
                                        
                                        <div className="tour-action">
                                            <span className="tour-price">
                                                <span className="currency">$</span>{hotel.price}
                                                <span className="duration">/night</span>
                                            </span>
                                            <Link to={`/hotel-details/${hotel.id}`} className="th-btn style4 th-icon">
                                                Book Now
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className="col-12 text-center py-5">
                            <div className="alert alert-info">
                                <h4>No Hotels Available</h4>
                                <p className="mb-0">Please check back later or contact us for more information.</p>
                            </div>
                        </div>
                    )}
                </div>

                {hotels.length > 0 && (
                    <div className="text-center mt-5">
                        <Link to="/hotels" className="th-btn style3">
                            View All Hotels
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
}

export default HotelListings;
